import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  AddressUpdateModel,
  DiscriminatedMatterClientUpdatedEvent,
  MatterClientPhoneNumberSchema,
  MatterClientSchema,
} from '@/services/api/generated/matterChangesService';

import {
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityTitle,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { FieldsChanged } from '../activity-feed-v2/FieldsChanged';
import { formatFieldName } from '../activity-feed-v2/utils';

export interface MatterClientUpdatedEventV2Props {
  event: DiscriminatedMatterClientUpdatedEvent;
}

/**
 * V2 renderer for Matter Client Updated events that uses the `previous` field
 * to show only what changed, instead of parsing the diff format.
 */
export const MatterClientUpdatedEventV2: React.FC<
  MatterClientUpdatedEventV2Props
> = ({ event }) => {
  const { matter_client, previous } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100">
          <PencilIcon className="h-4 w-4 text-indigo-600" />
        </div>
      }
    />
  );

  // Custom field renderer for client-specific fields
  const renderField = (
    name: keyof MatterClientSchema,
    value: MatterClientSchema[keyof MatterClientSchema],
  ) => {
    const label = formatFieldName(String(name));

    // Address object
    if (name === 'address' && value) {
      const address = value as AddressUpdateModel;
      const addressParts = [
        address.line1,
        address.line2,
        address.line3,
        [address.city, address.state, address.postcode]
          .filter(Boolean)
          .join(', '),
      ].filter(Boolean);

      return (
        <div className="space-y-1">
          <div className="font-medium text-sm text-gray-700">{label}</div>
          <div className="pl-4 text-sm">
            {addressParts.map((part, idx) => (
              <ActivityDescription key={idx}>{part}</ActivityDescription>
            ))}
          </div>
        </div>
      );
    }

    // Phone numbers array
    if (name === 'phone_numbers' && Array.isArray(value)) {
      const phones = value as MatterClientPhoneNumberSchema[];
      return (
        <div className="space-y-1">
          <div className="font-medium text-sm text-gray-700">{label}</div>
          <div className="pl-4 space-y-0.5">
            {phones.map((phone, idx: number) => (
              <ActivityDescription key={idx}>
                {phone.phone_number}
              </ActivityDescription>
            ))}
          </div>
        </div>
      );
    }

    // Date of birth - format nicely
    if (name === 'date_of_birth' && value && typeof value === 'string') {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {new Date(value).toLocaleDateString()}
        </ActivityDescription>
      );
    }

    // Default rendering for simple fields
    return (
      <ActivityDescription>
        <span className="font-medium">{label}:</span> {String(value)}
      </ActivityDescription>
    );
  };

  const body = (
    <ActivityBody>
      {matter_client?.name && (
        <ActivityTitle>{matter_client.name}</ActivityTitle>
      )}
      <FieldsChanged<MatterClientSchema>
        previous={previous}
        current={matter_client}
        fieldsToCheck={[
          'name',
          'email',
          'date_of_birth',
          'last_four_of_ssn',
          'preferred_language',
          'address',
          'phone_numbers',
        ]}
        renderField={renderField}
      />
    </ActivityBody>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Client Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
    />
  );
};
